import type { HomeyAPI, ItemProps, Mood } from 'homey-api';

import { __DEV__ } from '../../lib/__dev__';
import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';
import { selectedHomeyStore } from '../SelectedHomeyStore';

export type MoodProps = ItemProps<Mood>;
export type MoodsData = { [key: string]: Mood };
export type MoodsById = {
  [key: string]: MoodProps;
};

interface State {
  data: MoodsData | null;
  byId: MoodsById | null;
  isLoading: boolean;
  error: unknown;
}

export type MoodsStoreState = State;

export class MoodsStore extends BaseApiStore<State, Mood> {
  constructor() {
    super('moods');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (selectedHomeyStore.get().features?.moods.hasMoods === false) {
      this.set({
        ...this.createInitialState(),
        data: {},
        byId: {},
        isLoading: false,
      });

      return;
    }

    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ manager: api.moods });

      if (abortController.signal.aborted) return;

      const moodsPromise = api.moods.getMoods({
        $cache: cache,
      });

      const [moodsData] = await Promise.all([moodsPromise]);

      if (abortController.signal.aborted) return;

      const moods = Object.values(moodsData);

      const data: MoodsData = {};
      const byId: MoodsById = {};

      for (const moodInstance of moods) {
        const moodReference = moodInstance.getProps();

        data[moodReference.id] = moodInstance;
        byId[moodReference.id] = moodReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error: unknown) {
      if (abortController.signal.aborted) return;

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy(reason: string) {
    this.disconnectManager().catch(console.error);
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.moods.on('mood.create', this.handleCreate);
    api.moods.on('mood.update', this.handleUpdate);
    api.moods.on('mood.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.moods.off('mood.create', this.handleCreate);
    api.moods.off('mood.update', this.handleUpdate);
    api.moods.off('mood.delete', this.handleDelete);
  }

  getInstanceFromCache(mood: { id: string }) {
    return this.getCache()[mood.id];
  }

  getCache(): Readonly<Record<string, Mood>> {
    const api = this.getApi();

    return api.moods.getCachedItems('mood');
  }

  handleCreate = (createdMood: Mood) => {
    this.DEBUG && console.info(`create:mood:${createdMood.id}`);
    const state = this.get();
    const moodInstance = this.getInstanceFromCache(createdMood);

    if (moodInstance == null) return;

    const moodReference = moodInstance.getProps();

    this.set({
      data: {
        ...state.data,
        [createdMood.id]: moodInstance,
      },
      byId: {
        ...state.byId,
        [createdMood.id]: moodReference,
      },
    });
  };

  handleUpdate = (updatedMood: Mood) => {
    this.DEBUG && console.info(`update:mood:${updatedMood.id}`);

    this.addToQueue(updatedMood.id, updatedMood);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedMood] of Object.entries(updateQueue)) {
        const moodInstance = this.getInstanceFromCache(queuedMood);

        if (state.data == null) {
          this.DEBUG && console.info(`update:mood:missing-data`);
          continue;
        }

        if (moodInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:mood:unknown:${id}`);
          continue;
        }

        const moodReference = moodInstance.getProps();

        nextById[id] = moodReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:mood:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedMood: Pick<Mood, 'id'>) => {
    this.DEBUG && console.info(`delete:mood:${deletedMood.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:mood:missing-data`);
      return;
    }

    const moodInstance = state.data[deletedMood.id];

    if (moodInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[moodInstance.id];
    delete nextById[moodInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const moods = Object.values(cache ?? {});
    const data: MoodsData = {};
    const byId: MoodsById = {};

    for (const moodInstance of moods) {
      const moodReference = moodInstance.getProps();
      data[moodReference.id] = moodInstance;
      byId[moodReference.id] = moodReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }

  getMoods() {
    return this.get().byId;
  }
}

export const moodsStore = new MoodsStore();
